// Deduplicate the per-district 2026 candidate listings into a flat,
// alphabetically-sorted candidate array. A candidate that contests multiple
// districts (e.g. Adrian Delia in 7 and 8) appears once with both numbers.
//
// Built once at module init — the source data is small (<10 KB) and fixed
// for the duration of the campaign.

import rawData from '../data/candidates2026.json';

export type District = number | 'TBC';

export interface Candidate {
  name: string;
  party: string;
  districts: District[];
}

const SOURCE = rawData as Record<string, { name: string; party: string }[]>;

function compareDistricts(a: District, b: District): number {
  if (a === 'TBC') return 1;
  if (b === 'TBC') return -1;
  return a - b;
}

function buildList(): Candidate[] {
  const map = new Map<string, Candidate>();
  for (const [districtKey, list] of Object.entries(SOURCE)) {
    const district: District = districtKey === 'TBC' ? 'TBC' : Number(districtKey);
    for (const entry of list) {
      const key = `${entry.name}|${entry.party}`;
      const existing = map.get(key);
      if (existing) {
        existing.districts.push(district);
      } else {
        map.set(key, {
          name: entry.name,
          party: entry.party,
          districts: [district],
        });
      }
    }
  }
  const arr = Array.from(map.values());
  for (const c of arr) c.districts.sort(compareDistricts);
  // Sort by surname (last whitespace-separated token) then full name —
  // matches how Maltese rolls and ballot papers are typically ordered.
  arr.sort((a, b) => {
    const sa = surname(a.name);
    const sb = surname(b.name);
    return sa.localeCompare(sb, 'en', { sensitivity: 'base' }) ||
      a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
  });
  return arr;
}

function surname(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  return parts[parts.length - 1] ?? fullName;
}

export const CANDIDATES: Candidate[] = buildList();

export const PARTIES: string[] = (() => {
  const set = new Set<string>();
  for (const c of CANDIDATES) set.add(c.party);
  // Stable order — major parties first, then the rest alphabetically.
  const major = ['PL', 'PN', 'ADPD', 'Momentum', 'Imperium', 'Independent'];
  const known = major.filter((p) => set.has(p));
  const others = Array.from(set)
    .filter((p) => !major.includes(p))
    .sort();
  return [...known, ...others];
})();

export const DISTRICTS: District[] = (() => {
  const set = new Set<District>();
  for (const c of CANDIDATES) for (const d of c.districts) set.add(d);
  return Array.from(set).sort(compareDistricts);
})();

export const PARTY_VAR: Record<string, string> = {
  PL: 'var(--party-pl)',
  PN: 'var(--party-pn)',
  ADPD: 'var(--party-ad)',
  Momentum: 'var(--party-momentum)',
  Imperium: 'var(--party-imperium)',
  Independent: 'var(--party-ind)',
};

export const partyVar = (p: string): string => PARTY_VAR[p] ?? 'var(--muted)';
